<template>
  <div class="rich-text-editor-wrap" ref="wrapRef">
    <div
      v-if="editorReady"
      ref="editorBoxRef"
      class="rich-text-editor"
      :class="{ 'is-overlay': overlayVisible, 'is-disabled': disabled }"
      :style="overlayVisible ? overlayStyle : undefined"
    >
      <div v-if="overlayVisible" class="rich-text-editor__overlay-head">
        <span class="rich-text-editor__overlay-title">{{ label || '富文本编辑' }}</span>
        <el-button size="small" @click="closeOverlay">退出全屏</el-button>
      </div>
      <div class="rich-text-editor__toolbar-wrap">
        <Toolbar
          v-if="!disabled && editorRef"
          class="rich-text-editor__toolbar"
          :editor="editorRef"
          :defaultConfig="toolbarConfig"
          mode="default"
        />
        <button
          v-if="!disabled && !overlayVisible"
          type="button"
          class="rich-text-editor__expand"
          title="放大编辑"
          @click="openOverlay"
        >
          <el-icon><FullScreen /></el-icon>
        </button>
      </div>
      <Editor
        :key="`wangeditor-${editorKey}`"
        class="rich-text-editor__body"
        :style="editorBodyStyle"
        v-model="editorHtml"
        :defaultConfig="editorConfig"
        mode="default"
        @onCreated="handleCreated"
        @onChange="handleChange"
      />
    </div>
  </div>
</template>

<script setup>
import '@wangeditor/editor/dist/css/style.css'
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import { FullScreen } from '@element-plus/icons-vue'
import { getAuthToken, getUserId } from '@/utils/auth'
import { normalizeImgUrl } from '@/utils/mediaUrl'

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  label: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  minHeight: { type: String, default: '260px' },
  editorKey: { type: [String, Number], default: 'default' },
})

const emit = defineEmits(['update:modelValue'])

function normalizeHtmlForEmit(value) {
  const str = value == null ? '' : String(value)
  if (!str || str === '<p><br></p>' || str === '<p></p>') return ''
  return str
}

function normalizeHtmlForEditor(value) {
  const str = normalizeHtmlForEmit(value)
  return str || '<p><br></p>'
}

function isEditorAlive(editor) {
  if (!editor) return false
  try {
    return !editor.isDestroyed
  } catch {
    return false
  }
}

const wrapRef = ref(null)
const editorBoxRef = ref(null)
const editorRef = shallowRef()
const editorHtml = ref(normalizeHtmlForEditor(props.modelValue))
const editorReady = ref(true)
const overlayVisible = ref(false)
const overlayStyle = ref({})
const syncingFromParent = ref(false)

const editorBodyStyle = computed(() => (
  overlayVisible.value
    ? { height: 'calc(100% - 96px)', overflowY: 'hidden' }
    : { minHeight: props.minHeight, overflowY: 'hidden' }
))

const normalizeUrl = (url) => normalizeImgUrl(url)

const pickUploadUrl = (res) => {
  if (!res) return ''
  if (typeof res === 'string') return res
  if (res.url) return res.url
  if (res.data) {
    if (typeof res.data === 'string') return res.data
    if (typeof res.data === 'object' && res.data.url) return res.data.url
  }
  return ''
}

const buildUploadHeaders = () => {
  const headers = {}
  const token = getAuthToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
    headers.Token = token
  }
  const userId = getUserId()
  if (userId) headers.userId = userId
  return headers
}

const buildUploadMenuConf = () => ({
  server: '/api/upload/image',
  fieldName: 'file',
  headers: buildUploadHeaders(),
  customInsert(res, insertFn) {
    const url = normalizeUrl(pickUploadUrl(res))
    if (url) insertFn(url, '', '')
  },
})

const toolbarConfig = {
  toolbarKeys: [
    'headerSelect',
    'bold',
    'fontSize',
    'fontFamily',
    'italic',
    'underline',
    'through',
    '|',
    'color',
    'bgColor',
    '|',
    'bulletedList',
    'numberedList',
    'todo',
    '|',
    'justifyLeft',
    'justifyCenter',
    'justifyRight',
    '|',
    'insertLink',
    'uploadImage',
    'insertTable',
    'codeBlock',
    'divider',
    '|',
    'undo',
    'redo',
  ],
}

const editorConfig = {
  placeholder: props.placeholder || '请输入内容',
  readOnly: props.disabled,
  autoFocus: false,
  scroll: true,
  MENU_CONF: {
    uploadImage: buildUploadMenuConf(),
  },
}

const emitHtml = (val) => {
  if (syncingFromParent.value) return
  const normalized = normalizeHtmlForEmit(val)
  if (normalized !== normalizeHtmlForEmit(props.modelValue)) {
    emit('update:modelValue', normalized)
  }
}

const applyParentValue = (val) => {
  const next = normalizeHtmlForEditor(val)
  if (normalizeHtmlForEmit(editorHtml.value) === normalizeHtmlForEmit(val)) return
  syncingFromParent.value = true
  editorHtml.value = next
  queueMicrotask(() => {
    syncingFromParent.value = false
  })
}

watch(
  () => props.modelValue,
  (val) => {
    applyParentValue(val)
  },
  { immediate: true }
)

watch(
  () => props.editorKey,
  () => {
    editorRef.value = undefined
  }
)

watch(
  () => props.disabled,
  (val) => {
    const editor = editorRef.value
    if (!isEditorAlive(editor)) return
    if (val) editor.disable()
    else editor.enable()
  }
)

watch(editorHtml, (val) => {
  if (syncingFromParent.value) return
  emitHtml(val)
})

const handleCreated = (editor) => {
  editorRef.value = editor
  if (props.disabled) editor.disable()
}

const handleChange = () => {
  if (syncingFromParent.value) return
  emitHtml(editorHtml.value)
}

const resolveOverlayRect = () => {
  const shell = wrapRef.value?.closest('.form-shell')
  if (!shell) return null
  const rect = shell.getBoundingClientRect()
  return {
    position: 'fixed',
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    zIndex: 2000,
  }
}

const updateOverlayRect = () => {
  if (!overlayVisible.value) return
  const style = resolveOverlayRect()
  if (style) overlayStyle.value = style
}

const openOverlay = () => {
  if (props.disabled) return
  const style = resolveOverlayRect()
  if (!style) return
  overlayStyle.value = style
  overlayVisible.value = true
}

const closeOverlay = () => {
  overlayVisible.value = false
  overlayStyle.value = {}
  updateOverlayRect()
}

const onKeydown = (event) => {
  if (!overlayVisible.value) return
  if (event.key === 'Escape') {
    event.preventDefault()
    closeOverlay()
  }
}

const onWindowChange = () => {
  updateOverlayRect()
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
  window.addEventListener('resize', onWindowChange)
  window.addEventListener('scroll', onWindowChange, true)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', onWindowChange)
  window.removeEventListener('scroll', onWindowChange, true)
  if (isEditorAlive(editorRef.value)) {
    editorRef.value.destroy()
    editorRef.value = undefined
  }
})
</script>

<style scoped>
.rich-text-editor-wrap {
  width: 100%;
}
.rich-text-editor {
  width: 100%;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
  display: flex;
  flex-direction: column;
}
.rich-text-editor.is-overlay {
  border-radius: 0;
  border: none;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}
.rich-text-editor__overlay-head {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
  background: #fff;
}
.rich-text-editor__overlay-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.rich-text-editor__toolbar-wrap {
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
}
.rich-text-editor__toolbar {
  flex: 1;
  min-width: 0;
  border-bottom: none;
}
.rich-text-editor__expand {
  flex-shrink: 0;
  width: 40px;
  border: none;
  border-left: 1px solid #e8e8e8;
  background: #fafafa;
  color: #606266;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.rich-text-editor__expand:hover {
  background: #f0f2f5;
  color: #409eff;
}
.rich-text-editor__body {
  flex: 1;
  min-height: 0;
}
.rich-text-editor.is-disabled {
  opacity: 0.75;
}
.rich-text-editor :deep(.w-e-text-container) {
  background: #fff;
}
.rich-text-editor.is-overlay :deep(.w-e-scroll) {
  height: 100% !important;
}
</style>
